import React, { useRef } from 'react'
import { LuAlertTriangle, LuArrowUpRight, LuDownload, LuRotateCw, LuX } from 'react-icons/lu'
import { TABLE_CARD_INNER, TABLE_HEAD_ROW } from '../../../components/Table/tableCardClass'
import SortableTableHeader from '../../../components/Table/SortableTableHeader'
import TableSkeletonRows from '../../../components/Skeleton/TableSkeletonRows'
import TableFooter from '../../../components/Pagination/TableFooter'
import { downloadCsv } from '../../../utils/csv'
import { formatMoney } from '../../../utils/amount'
import {
  drilldownColumns,
  clientIdOf,
  csvFilename,
  pageableCount,
  CONTACT_METRICS,
  ACTIVITY_TABS,
  MAX_DRILLDOWN_OFFSET,
  EMPTY_CELL,
} from './drilldownColumns'

const FOCUS_RING = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-main'

const ClientLinkRow = ({ row, columns, clientId, href, onOpenClient }) => {
  const clientLink = useRef(null)
  const pressed = useRef(false)
  const focusedByPointer = useRef(false)
  const leftWindow = useRef(false)
  const focusColumn = columns.find((column) => column.key === 'client') ?? columns[0]
  const shown = String(focusColumn?.value(row) ?? '').trim()
  const name = shown && shown !== EMPTY_CELL ? shown : 'client'

  const openInSameTab = (event) => {
    if (!onOpenClient || event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    onOpenClient(clientId)
  }

  const onLinkKeyDown = (event) => {
    if (!onOpenClient || event.key !== ' ' || focusedByPointer.current) return
    event.preventDefault()
    onOpenClient(clientId)
  }

  return (
    <tr
      className="border-b border-divider hover:bg-action-hover cursor-pointer"
      onPointerDown={() => { pressed.current = true }}
      onPointerUp={() => { pressed.current = false }}
      onPointerCancel={() => { pressed.current = false }}
    >
      {columns.map((column) => (
        <td key={column.key} className="pl-5 py-[14px] text-[14px] text-text-primary whitespace-nowrap relative">
          {column === focusColumn ? (
            <span className="inline-flex items-center gap-1.5">
              <a
                ref={clientLink}
                href={href}
                aria-label={`Open ${name}`}
                onClick={openInSameTab}
                onKeyDown={onLinkKeyDown}
                onFocus={() => {
                  if (leftWindow.current) {
                    leftWindow.current = false
                    return
                  }
                  focusedByPointer.current = pressed.current
                  pressed.current = false
                }}
                onBlur={() => {
                  if (document.hasFocus()) {
                    focusedByPointer.current = false
                    return
                  }
                  leftWindow.current = true
                }}
                className={`text-text-primary no-underline after:absolute after:inset-0 after:content-[''] ${FOCUS_RING}`}
              >
                {column.value(row)}
              </a>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${name} in a new tab`}
                className={`relative z-[1] inline-flex items-center rounded p-[5px] -m-[5px] text-text-secondary hover:text-primary-main ${FOCUS_RING}`}
              >
                <LuArrowUpRight size={14} aria-hidden="true" />
              </a>
            </span>
          ) : (
            <>
              {column.value(row)}
              <a
                href={href}
                tabIndex={-1}
                aria-hidden="true"
                aria-label={`Open ${name}`}
                onClick={openInSameTab}
                onFocus={() => clientLink.current?.focus({ preventScroll: true })}
                className="absolute inset-0"
              />
            </>
          )}
        </td>
      ))}
    </tr>
  )
}

const DrilldownPanel = ({
  metric,
  metricLabel,
  periodLabel,
  day,
  onClearDay,
  rows = [],
  // Supplied by the container. Kept as a callback rather than useNavigate here so the panel
  // stays presentational and its tests need no router.
  onOpenClient,
  clientHref,
  meta,
  loading = false,
  error = null,
  onRetry,
  sortOrder = 'desc',
  onSortOrderChange,
  page = 1,
  limit = 25,
  setPage,
  setLimit,
  canExport = true,
  showMoney = true,
  onMetricChange,
  pending = false,
  pendingHint = null,
}) => {
  const columns = drilldownColumns(metric, showMoney, rows)
  const sortField = columns.find((column) => column.sortable)?.key
  // Everything below meta belongs to the selection the rows came from. While a newer selection has
  // not answered, none of it may be presented as describing what is on screen — including the
  // export, whose filename is built from the CURRENT metric.
  const total = pending ? 0 : meta?.total ?? 0
  const hasRows = !pending && rows.length > 0
  const activity = pending ? null : meta?.activity
  const showTabs = CONTACT_METRICS.includes(metric) && Boolean(onMetricChange)
  const reachablePages = pageableCount(meta?.pageCount, limit)
  const truncated = (meta?.pageCount || 0) > reachablePages

  const handleSort = () => {
    if (!onSortOrderChange) return
    onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')
  }

  const handleExport = () => {
    downloadCsv(csvFilename(metric, day || periodLabel), columns, rows)
  }

  return (
    <div className={TABLE_CARD_INNER}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 px-5 py-4 border-b border-divider">
        <div className="min-w-0">
          <h4 className="font-semibold text-[15px] text-text-primary">{metricLabel} records</h4>
          {!pending && (
            <p className="text-[13px] text-text-secondary mt-0.5">
              {[
                day ? `${day} · one day` : periodLabel,
                `${total} records`,
                meta?.totalAmount !== undefined ? formatMoney(meta.totalAmount) : null,
                meta?.nsf !== undefined ? `${meta.nsf} NSF · ${meta.declined} declined` : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {day && onClearDay && (
            <button
              type="button"
              onClick={onClearDay}
              className="inline-flex items-center gap-1.5 rounded-lg border border-divider bg-surface px-3 h-9 text-[13px] font-medium text-text-primary hover:bg-divider transition-colors"
            >
              <LuX size={14} /> Clear day
            </button>
          )}
          {canExport && hasRows && !error && (
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 rounded-lg border border-divider bg-surface px-3 h-9 text-[13px] font-medium text-text-primary hover:bg-divider transition-colors"
            >
              <LuDownload size={14} /> Export page ({rows.length} rows)
            </button>
          )}
        </div>
      </div>

      {showTabs && (
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-divider">
          {ACTIVITY_TABS.map(({ value, label, countKey }) => (
            <button
              key={value}
              type="button"
              aria-pressed={metric === value}
              onClick={() => onMetricChange(value)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 h-8 text-[13px] font-medium transition-colors ${
                metric === value
                  ? 'border-primary-main text-primary-main bg-[rgba(94,106,210,0.10)]'
                  : 'border-divider text-text-secondary hover:bg-divider'
              }`}
            >
              {label}
              {activity && (
                <span className="rounded-full bg-[var(--color-bg-subtle)] px-2 text-[12px] tabular-nums">
                  {activity[countKey]}
                </span>
              )}
            </button>
          ))}
          {activity && (
            <span className="ml-auto text-[13px] text-text-secondary">
              Net new <span className="font-semibold text-text-primary tabular-nums">
                {activity.netNew > 0 ? `+${activity.netNew}` : activity.netNew}
              </span>
            </span>
          )}
        </div>
      )}

      {error ? (
        <div className="h-[280px] flex flex-col items-center justify-center text-center gap-3 px-5">
          <span className="flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(240,68,56,0.12)' }}>
            <LuAlertTriangle size={20} style={{ color: 'var(--color-error-main)' /* non-text: 20px icon, answers to 3:1 */ }} />
          </span>
          <div>
            <p className="text-[15px] font-semibold text-text-primary">Couldn’t load these records</p>
            <p className="text-[13px] text-text-secondary mt-1">{error}</p>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 rounded-lg border border-divider bg-surface px-4 h-9 text-[13px] font-medium text-text-primary hover:bg-divider transition-colors"
            >
              <LuRotateCw size={16} /> Retry
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={TABLE_HEAD_ROW}>
                  {columns.map((column) => (
                    <SortableTableHeader
                      key={column.key}
                      label={column.label}
                      field={column.key}
                      currentSortField={column.sortable ? sortField : ''}
                      currentSortOrder={column.sortable ? sortOrder : ''}
                      onSort={handleSort}
                      sortable={Boolean(column.sortable)}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && !hasRows ? (
                  <TableSkeletonRows rows={6} colSpan={columns.length} />
                ) : pending ? (
                  <tr>
                    <td colSpan={columns.length} className="px-5 py-10 text-center">
                      <p className="text-[15px] font-semibold text-text-primary">No records loaded for this selection</p>
                      <p className="text-[13px] text-text-secondary mt-1">
                        {pendingHint || 'Finish choosing the filters and the records will load.'}
                      </p>
                    </td>
                  </tr>
                ) : !hasRows ? (
                  <tr>
                    <td colSpan={columns.length} className="px-5 py-10 text-center">
                      <p className="text-[15px] font-semibold text-text-primary">No records for this selection</p>
                      <p className="text-[13px] text-text-secondary mt-1">
                        {day
                          ? 'This day has no records — the chart shows zero for it too.'
                          : 'This metric is zero for the selected period and filters.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    // Mendy, 4 Sep 2026: "make this clickable as well please everyone loves this
                    // feature but they want to be able to open the client." Reading a name here
                    // used to mean copying it, going to Contacts and searching for it.
                    //
                    // A row whose client cannot be established stays un-clickable rather than
                    // linking somewhere wrong — see clientIdOf.
                    const clientId = clientIdOf(row)
                    const openClient = () => clientId && onOpenClient?.(clientId)

                    if (typeof clientHref === 'function' && clientId) {
                      return (
                        <ClientLinkRow
                          key={row._id}
                          row={row}
                          columns={columns}
                          clientId={clientId}
                          href={clientHref(clientId)}
                          onOpenClient={onOpenClient}
                        />
                      )
                    }

                    return (
                      <tr
                        key={row._id}
                        onClick={clientId ? openClient : undefined}
                        onKeyDown={clientId ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openClient()
                          }
                        } : undefined}
                        tabIndex={clientId ? 0 : undefined}
                        role={clientId ? 'link' : undefined}
                        aria-label={clientId ? 'Open client' : undefined}
                        className={`border-b border-divider hover:bg-action-hover ${clientId ? 'cursor-pointer' : ''}`}
                      >
                        {columns.map((column) => (
                          <td key={column.key} className="pl-5 py-[14px] text-[14px] text-text-primary whitespace-nowrap">
                            {column.value(row)}
                          </td>
                        ))}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {truncated && !pending && (
            <p className="px-5 pt-3 text-[13px] text-text-secondary">
              Only the first {MAX_DRILLDOWN_OFFSET.toLocaleString()} records are pageable. Narrow the
              period or the filters to reach the rest.
            </p>
          )}

          {!pending && (
            <TableFooter
              page={page}
              limit={limit}
              total={total}
              pageCount={reachablePages}
              setPage={setPage}
              setLimit={setLimit}
            />
          )}
        </>
      )}
    </div>
  )
}

export default DrilldownPanel
